/**
 * Task 7a — Card payment obligation (docs/04 §2).
 * Pure: statement cycles + posted ledger only. Never estimates.
 * derivedPaidInFullByDueDate null means unknown, never false.
 */

import { asCents, type Cents } from './money';
import {
  derivedPaidInFullByDueDate,
  type PaymentAllocationInput,
  type PaymentHistoryCoverageInput,
  type StatementCycleInput,
} from './cycles';
import type { CardPaymentInput } from './state';
import { businessDate, compareBusinessDates, diffDays, type BusinessDate } from './time';

/** Cycle statuses that carry a contractual issued-statement obligation (docs/04 §2). */
export const DUE_NOW_STATUSES = new Set([
  'statement_issued',
  'payment_due',
  'partially_paid',
  'past_due',
]);

export type ObligationTransactionInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  kind: string;
  status: string;
  amount_cents: Cents;
  posted_date: string | null;
  transaction_date: string;
  /** Required to attribute spend to an open cycle for not_yet_due. */
  statement_cycle_id: string | null;
};

/** Latest autopay observation fields needed for autopay_expected (docs/01). */
export type AutopayObservationInput = {
  card_id: string;
  deleted_at: string | null;
  observed_at: string;
  enabled: boolean | null;
  autopay_kind: string;
  fixed_amount_cents: Cents | null;
};

export type IngestStatementCycleInput = {
  id: string;
  card_id: string;
  cycle_start_date: string;
  cycle_end_date: string;
  statement_date: string;
  payment_due_date: string;
  statement_balance_cents: Cents;
  minimum_payment_cents: Cents | null;
  /** Required for non-projected cycles (docs/01 actual_cycle_needs_source). */
  source_id: string;
  autopay_scheduled_cents?: Cents | null;
  autopay_scheduled_date?: string | null;
  status?: 'statement_issued' | 'payment_due' | 'partially_paid' | 'past_due';
};

export type CardObligation = {
  card_id: string;
  /** Σ remaining_balance of issued/due cycles. Contractual obligation. */
  due_now_cents: Cents;
  /** True when a due-status cycle has null statement_balance (cannot invent). */
  due_now_incomplete: boolean;
  /** Σ posted transactions on the open cycle (ledger, not estimates). */
  not_yet_due_cents: Cents;
  /** Σ pending transactions on this card. */
  pending_cents: Cents;
  /** Σ posted payment allocations to issued/due cycles. */
  already_covered_cents: Cents;
  /** Latest issued cycle's minimum_payment_cents (null if unknown). */
  minimum_due_cents: Cents | null;
  pay_statement_cents: Cents;
  pay_to_zero_cents: Cents;
  next_due_date: BusinessDate | null;
  days_until_due: number | null;
  autopay_expected_cents: Cents;
  /** due_now − autopay_expected when positive; else 0. */
  autopay_shortfall_cents: Cents;
  /** Per due-status cycle: remaining and paid-in-full derivation (null = unknown). */
  cycles: readonly CycleObligationRow[];
};

export type CycleObligationRow = {
  statement_cycle_id: string;
  status: string;
  remaining_balance_cents: Cents | null;
  paid_in_full_by_due_date: boolean | null;
  payment_due_date: string | null;
};

const DEBIT_KINDS = new Set([
  'purchase',
  'fee',
  'interest',
  'cash_advance',
  'balance_transfer',
  'adjustment',
]);
const CREDIT_KINDS = new Set(['refund', 'statement_credit']);

/**
 * Build a real (non-projected) statement cycle from statement evidence.
 * Throws if source_id is missing or the date window is invalid.
 */
export function ingestStatementCycle(input: IngestStatementCycleInput): StatementCycleInput {
  if (input.source_id.trim().length === 0) {
    throw new Error('ingestStatementCycle requires source_id for a non-projected cycle');
  }
  const start = businessDate(input.cycle_start_date);
  const end = businessDate(input.cycle_end_date);
  if (compareBusinessDates(start, end) > 0) {
    throw new Error('cycle_start_date must be <= cycle_end_date');
  }
  const statement = businessDate(input.statement_date);
  const due = businessDate(input.payment_due_date);
  if (compareBusinessDates(due, statement) < 0) {
    throw new Error('payment_due_date must be >= statement_date');
  }

  return {
    id: input.id,
    card_id: input.card_id,
    deleted_at: null,
    cycle_start_date: start,
    cycle_end_date: end,
    statement_date: statement,
    payment_due_date: due,
    is_projected: false,
    statement_balance_cents: input.statement_balance_cents,
    minimum_payment_cents: input.minimum_payment_cents,
    source_id: input.source_id,
    autopay_scheduled_cents: input.autopay_scheduled_cents ?? null,
    autopay_scheduled_date: input.autopay_scheduled_date ?? null,
    status: input.status ?? 'statement_issued',
    paid_in_full_override: null,
  };
}

/**
 * Remaining contractual balance on a cycle:
 * statement_balance − Σ posted payment allocations to the cycle (all posted dates).
 * Null when statement_balance is unknown — never invent 0.
 */
export function remainingBalanceCents(
  cycle: StatementCycleInput,
  payments: readonly CardPaymentInput[],
  allocations: readonly PaymentAllocationInput[],
): Cents | null {
  if (cycle.statement_balance_cents === null) return null;
  let paid = 0n;
  for (const al of allocations) {
    if (al.deleted_at !== null) continue;
    if (al.statement_cycle_id !== cycle.id) continue;
    const pay = payments.find((p) => p.id === al.payment_id && p.deleted_at === null);
    if (!pay || pay.status !== 'posted') continue;
    paid += al.amount_cents;
  }
  const rem = cycle.statement_balance_cents - paid;
  return asCents(rem > 0n ? rem : 0n);
}

function postedAllocationsToCycle(
  cycleId: string,
  payments: readonly CardPaymentInput[],
  allocations: readonly PaymentAllocationInput[],
): Cents {
  let sum = 0n;
  for (const al of allocations) {
    if (al.deleted_at !== null) continue;
    if (al.statement_cycle_id !== cycleId) continue;
    const pay = payments.find((p) => p.id === al.payment_id && p.deleted_at === null);
    if (!pay || pay.status !== 'posted') continue;
    sum += al.amount_cents;
  }
  return asCents(sum);
}

function latestAutopay(
  cardId: string,
  observations: readonly AutopayObservationInput[],
): AutopayObservationInput | null {
  const live = observations
    .filter((o) => o.card_id === cardId && o.deleted_at === null)
    .slice()
    .sort((a, b) => (a.observed_at < b.observed_at ? 1 : a.observed_at > b.observed_at ? -1 : 0));
  return live[0] ?? null;
}

function autopayExpected(
  dueNow: Cents,
  minimumDue: Cents | null,
  obs: AutopayObservationInput | null,
): Cents {
  if (obs === null) return asCents(0n);
  if (obs.enabled === false) return asCents(0n);
  switch (obs.autopay_kind) {
    case 'none':
      return asCents(0n);
    case 'fixed':
      return obs.fixed_amount_cents ?? asCents(0n);
    case 'minimum':
      return minimumDue ?? asCents(0n);
    case 'statement_balance':
      return dueNow;
    case 'current_balance':
      // Requires an observed balance input — not estimated. Without it, do not invent.
      return asCents(0n);
    case 'unknown':
      return asCents(0n);
    default:
      return asCents(0n);
  }
}

export type ComputeCardObligationArgs = {
  cardId: string;
  asOf: BusinessDate;
  cycles: readonly StatementCycleInput[];
  payments: readonly CardPaymentInput[];
  allocations: readonly PaymentAllocationInput[];
  coverage: readonly PaymentHistoryCoverageInput[];
  transactions: readonly ObligationTransactionInput[];
  autopayObservations: readonly AutopayObservationInput[];
};

/**
 * Per-card obligation object (docs/04 §2).
 * Uses real cycles and posted payments/allocations only — never getCardState estimates.
 */
export function computeCardObligation(args: ComputeCardObligationArgs): CardObligation {
  const {
    cardId,
    asOf,
    cycles,
    payments,
    allocations,
    coverage,
    transactions,
    autopayObservations,
  } = args;

  const liveCycles = cycles.filter((c) => c.card_id === cardId && c.deleted_at === null);

  const dueCycles = liveCycles.filter((c) => !c.is_projected && DUE_NOW_STATUSES.has(c.status));

  const cycleRows: CycleObligationRow[] = [];
  let dueNow = 0n;
  let dueIncomplete = false;
  let alreadyCovered = 0n;

  for (const cycle of dueCycles) {
    const remaining = remainingBalanceCents(cycle, payments, allocations);
    const paidInFull = derivedPaidInFullByDueDate(cycle, payments, allocations, coverage);
    // Explicit: null stays null (unknown). Never coerce to false for obligation math.
    cycleRows.push({
      statement_cycle_id: cycle.id,
      status: cycle.status,
      remaining_balance_cents: remaining,
      paid_in_full_by_due_date: paidInFull,
      payment_due_date: cycle.payment_due_date,
    });
    if (remaining === null) {
      dueIncomplete = true;
    } else {
      dueNow += remaining;
    }
    alreadyCovered += postedAllocationsToCycle(cycle.id, payments, allocations);
  }

  const openCycle = liveCycles.find((c) => c.status === 'open' && !c.is_projected);
  let notYetDue = 0n;
  if (openCycle !== undefined) {
    for (const txn of transactions) {
      if (txn.deleted_at !== null) continue;
      if (txn.card_id !== cardId) continue;
      if (txn.statement_cycle_id !== openCycle.id) continue;
      if (txn.status !== 'posted') continue;
      if (DEBIT_KINDS.has(txn.kind)) notYetDue += txn.amount_cents;
      else if (CREDIT_KINDS.has(txn.kind)) notYetDue -= txn.amount_cents;
    }
    if (notYetDue < 0n) notYetDue = 0n;
  }

  let pending = 0n;
  for (const txn of transactions) {
    if (txn.deleted_at !== null) continue;
    if (txn.card_id !== cardId) continue;
    if (txn.status !== 'pending') continue;
    if (DEBIT_KINDS.has(txn.kind)) pending += txn.amount_cents;
  }

  // Latest issued cycle by cycle_end_date for minimum_due
  const issuedSorted = dueCycles.slice().sort((a, b) => {
    const byEnd = compareBusinessDates(
      businessDate(b.cycle_end_date),
      businessDate(a.cycle_end_date),
    );
    if (byEnd !== 0) return byEnd;
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
  const latestIssued = issuedSorted[0];
  const minimumDue = latestIssued?.minimum_payment_cents ?? null;

  // Earliest unpaid cycle's due date (remaining > 0)
  let nextDue: BusinessDate | null = null;
  for (const row of cycleRows) {
    if (row.remaining_balance_cents === null || row.remaining_balance_cents <= 0n) continue;
    if (row.payment_due_date === null) continue;
    const d = businessDate(row.payment_due_date);
    if (nextDue === null || compareBusinessDates(d, nextDue) < 0) nextDue = d;
  }

  const dueNowCents = asCents(dueNow);
  const autopay = latestAutopay(cardId, autopayObservations);
  const expected = autopayExpected(dueNowCents, minimumDue, autopay);
  const shortfallRaw = dueNowCents - expected;
  const shortfall = asCents(shortfallRaw > 0n ? shortfallRaw : 0n);

  return {
    card_id: cardId,
    due_now_cents: dueNowCents,
    due_now_incomplete: dueIncomplete,
    not_yet_due_cents: asCents(notYetDue),
    pending_cents: asCents(pending),
    already_covered_cents: asCents(alreadyCovered),
    minimum_due_cents: minimumDue,
    pay_statement_cents: dueNowCents,
    pay_to_zero_cents: asCents(dueNow + notYetDue),
    next_due_date: nextDue,
    days_until_due: nextDue === null ? null : diffDays(asOf, nextDue),
    autopay_expected_cents: expected,
    autopay_shortfall_cents: shortfall,
    cycles: cycleRows,
  };
}
